// =============================================================================
// ui/tabs/ResultsPlaybook.tsx — the Results board's pattern-by-pattern table. #58.
// -----------------------------------------------------------------------------
// One row per pattern, ranked. This is the board's decision surface: it is where
// a trader decides which setup to keep trading and which one to stop, so every
// cell here has to be either a number that arrived computed or an em-dash. The
// columns, in beta's order, read off the RENDERED page (not the source, which
// still carries markup for things the board no longer shows):
//
//   Pattern · Samples · Win rate · Expectancy · Net pts · PF · E-ratio ·
//   Avg MFE / MAE · Curve
//
// ── 🚨 NULLS SORT LAST, IN BOTH DIRECTIONS — A DELIBERATE FIX, NOT A PORT ──
//
// Beta sorts with `(SORTS[k](a) ?? -1e9) - (SORTS[k](b) ?? -1e9)`
// (backtest-dashboard.html:676), which treats "unmeasured" as minus infinity. On
// a descending sort that hides those patterns at the bottom, which is harmless.
// On an ASCENDING sort it floats them to the TOP, so a pattern with no profit
// factor — one that has simply never lost, or has too little data to divide by —
// is presented as the single worst performer on the board. That is the opposite
// of what the absence means, and it is the kind of ranking a trader acts on.
//
// Here a null is pushed to the end whichever way the arrow points. The reversal
// therefore reverses the MEASURED rows only. That divergence is intentional; do
// not "restore parity" on it.
//
// ── 🚨 THE LIVE ROW CANNOT SHOW BETA'S GAP NUMBER, AND DOES NOT FAKE ONE ──
//
// Beta's live sub-row prints `Math.round(backtestWinRate − liveWinRate)` as
// "−7 pts win-rate", coloured red, plus the clause "execution slippage on a
// taught setup". That subtraction is a statistic, and `ui/` computes nothing —
// the number is not on these props and is not invented here. The row shows the
// live win rate beside the backtest one and says so plainly; the signed gap (and
// with it the red/green and the slippage clause, which beta prints even when
// live is BEATING the backtest) waits on a selector that supplies it.
//
// ⚠ Beta's own "nothing to compare against" copy is kept verbatim for the case
// where the backtest side has no decided samples.
//
// ── ⚠ TWO SMALL THINGS THAT LOOK LIKE OVERSIGHTS AND ARE NOT ──
//
// • The win-rate bar is a CSS width, not an SVG, exactly as beta draws it. It is
//   the one bar on the board that has to line up across rows.
// • Beta prints a second line under the pattern name — its CATEGORY. A
//   `BoardGroup` carries only `key` and `label`, so there is nothing truthful to
//   put there and the line is omitted rather than filled with the id.
// =============================================================================

import { Fragment, useMemo, useState } from 'react';

import type { BoardGroup, BoardStats } from '../../selectors/backtestBoard';
import { num, pct } from '../format';

/** Beta's six sortable columns, by its own keys. */
type SortKey = 'n' | 'wr' | 'exp' | 'net' | 'pf' | 'er';

const SORTS: Record<SortKey, (s: BoardStats) => number | null> = {
  n: (s) => s.n,
  wr: (s) => s.winRate,
  exp: (s) => s.expectancy,
  net: (s) => s.net,
  pf: (s) => s.profitFactor,
  er: (s) => s.edgeRatio,
};

/** `-1` descending, `1` ascending. Beta's own encoding, and its default is -1. */
type SortDir = -1 | 1;

/**
 * ⚠ Beta's sample-confidence thresholds, kept as-is: 30+ reads as a real
 * sample, 10+ as provisional, below that as a number not to lean on.
 */
function sampleClass(n: number): string {
  if (n >= 30) return 'bd-pb-n bd-pb-n-ok';
  if (n >= 10) return 'bd-pb-n bd-pb-n-mid';
  return 'bd-pb-n';
}

/** `+` for zero and up; the minus comes from `num`. Matches ResultsKpis. */
function signed(v: number, decimals: number): string {
  return (v >= 0 ? '+' : '') + num(v, decimals);
}

const SPARK_W = 80;
const SPARK_H = 22;

/**
 * The cumulative curve, as a path.
 *
 * Presentation maths only — the series arrives computed; this turns values into
 * viewBox coordinates. Beta's framing is kept: the floor is min(0, …) so a
 * curve that never goes negative still sits on the zero line rather than being
 * stretched to fill the box, and the ceiling is max(1, …) so a flat-zero series
 * does not divide by nothing.
 */
function sparkPath(series: readonly number[]): string {
  if (series.length === 0) return '';
  const top = Math.max(1, ...series);
  const floor = Math.min(0, ...series);
  const span = top - floor || 1;
  const steps = series.length - 1 || 1;
  return series
    .map((v, i) => {
      const x = ((i / steps) * SPARK_W).toFixed(1);
      const y = (SPARK_H - ((v - floor) / span) * SPARK_H).toFixed(1);
      return `${i ? 'L' : 'M'}${x} ${y}`;
    })
    .join(' ');
}

function Spark({ series }: { series: readonly number[] }) {
  const d = sparkPath(series);
  if (!d) return null;
  // ⚠ The sign of the LAST point, not of the slope — the curve is cumulative, so
  // this asks "is the pattern in the black overall", which is what the colour is
  // read as.
  const up = (series[series.length - 1] ?? 0) >= 0;
  return (
    <svg
      className="bd-pb-spark"
      viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
      aria-hidden="true"
      focusable="false"
    >
      {/* ⚠ Paint on the element, not in a class — see `svgSnapshotSafe.test.ts`.
          The Results tab has no snapshot button today, so this was the same
          defect as the Day Overview chart lying dormant rather than a live one.
          Fixed with it so the guard needs no exception list. */}
      <path d={d} fill="none" stroke={up ? 'var(--green)' : 'var(--red)'} />
    </svg>
  );
}

/**
 * A sortable column header.
 *
 * ⚠ Declared at module scope on purpose. Defined inside `ResultsPlaybook` it
 * would be a NEW component type on every render, so React would tear the header
 * cells down and rebuild them after each sort — which throws away keyboard focus
 * mid-interaction and makes the column unusable without a mouse.
 */
function SortTh(
  { k, label, sortKey, sortDir, onSort }:
  { k: SortKey; label: string; sortKey: SortKey; sortDir: SortDir; onSort: (k: SortKey) => void },
) {
  const active = sortKey === k;
  return (
    <th
      scope="col"
      className="bd-pb-th bd-pb-th-sortable"
      aria-sort={active ? (sortDir === -1 ? 'descending' : 'ascending') : 'none'}
      tabIndex={0}
      onClick={() => onSort(k)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSort(k);
        }
      }}
    >
      {label}
      {active ? <span className="bd-pb-caret"> {sortDir === -1 ? '▾' : '▴'}</span> : null}
    </th>
  );
}

/** The win-rate cell: the number, then beta's CSS-width bar behind it. */
function WinRateCell({ rate, tone }: { rate: number | null; tone: 'live' | 'backtest' }) {
  // Presentation width. Beta's clamp: never wider than the track, and never so
  // thin it disappears — a 1% win rate must still render as a visible sliver,
  // because an empty track reads as "no data" rather than "almost never wins".
  const width = Math.max(3, Math.min(100, rate ?? 0));
  const good = tone === 'backtest' && rate != null && rate >= 50;
  return (
    <div className="bd-pb-wrcell">
      <span className={good ? 'bd-pb-pos' : 'bd-pb-muted'}>{pct(rate, 0)}</span>
      <span className="bd-pb-wrbar">
        <i
          className={good ? 'bd-pb-wrfill' : 'bd-pb-wrfill-down'}
          style={{ width: `${width}%` }}
        />
      </span>
    </div>
  );
}

export interface ResultsPlaybookProps {
  groups: readonly BoardGroup[];
  /** The focused pattern id, or null when the board is showing everything. */
  focused: string | null;
  /** Clicking the focused row again clears it — hence the nullable argument. */
  onFocus: (patternId: string | null) => void;
  /**
   * Present when the Live-comparison toggle is on. Keyed by pattern id; a
   * pattern absent from the map has no live fills and gets no sub-row.
   */
  live?: ReadonlyMap<string, { winRate: number | null; n: number }> | undefined;
  /** That pattern's cumulative points series, already computed. */
  sparkline: (g: BoardGroup) => readonly number[];
}

export function ResultsPlaybook({
  groups, focused, onFocus, live, sparkline,
}: ResultsPlaybookProps) {
  // ⚠ Sorting is this component's own state, not the board's. It changes the
  // order of the ranking and nothing else — no other card reacts to it, and it
  // deliberately survives a focus click.
  const [sortKey, setSortKey] = useState<SortKey>('exp');
  const [sortDir, setSortDir] = useState<SortDir>(-1);

  const rows = useMemo(() => {
    const get = SORTS[sortKey];
    // Beta drops empty groups before ranking; an empty group is a pattern that
    // matched the filter with nothing in it, and it has no place in a ranking.
    return [...groups]
      .filter((g) => g.stats.n > 0)
      .sort((a, b) => {
        const av = get(a.stats);
        const bv = get(b.stats);
        // 🚨 Nulls last both ways. See the header — this is the fix, not a port.
        if (av == null && bv == null) return a.label.localeCompare(b.label);
        if (av == null) return 1;
        if (bv == null) return -1;
        if (av === bv) return a.label.localeCompare(b.label);
        return (av - bv) * sortDir;
      });
  }, [groups, sortKey, sortDir]);

  function toggleSort(k: SortKey) {
    if (k === sortKey) {
      setSortDir((d) => (d === -1 ? 1 : -1));
    } else {
      // A fresh column always opens descending — "best first" is what every one
      // of these six columns means on first click.
      setSortKey(k);
      setSortDir(-1);
    }
  }

  const th = (k: SortKey, label: string) => (
    <SortTh k={k} label={label} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
  );

  return (
    <div className="bd-pb-card" data-testid="results-playbook">
      <div className="bd-pb-head">
        <span className="bd-pb-eyebrow">Playbook</span>
        <h3 className="bd-pb-title">Pattern-by-pattern performance</h3>
        <span className="bd-pb-note">Click a row to focus the whole board on that pattern</span>
      </div>

      <div className="bd-pb-scroll">
        <table className="bd-pb-table">
          <thead>
            <tr>
              <th scope="col" className="bd-pb-th bd-pb-th-l">Pattern</th>
              {th('n', 'Samples')}
              {th('wr', 'Win rate')}
              {th('exp', 'Expectancy')}
              {th('net', 'Net pts')}
              {th('pf', 'PF')}
              {th('er', 'E-ratio')}
              <th scope="col" className="bd-pb-th">Avg MFE / MAE</th>
              <th scope="col" className="bd-pb-th">Curve</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td className="bd-pb-empty" colSpan={9}>
                  No patterns with samples in this slice.
                </td>
              </tr>
            ) : null}

            {rows.map((g) => {
              const s = g.stats;
              const isFocused = focused === g.key;
              const l = live?.get(g.key);

              return (
                // ⚠ A keyed Fragment, not `<>`: a pattern renders as TWO sibling
                // rows when the live toggle is on, and the shorthand cannot carry
                // a key — React would then re-key the whole list on every sort.
                <Fragment key={g.key}>
                  <tr
                    className={`bd-pb-row${isFocused ? ' bd-pb-row-active' : ''}`}
                    tabIndex={0}
                    aria-current={isFocused || undefined}
                    // Clicking the focused row clears the focus, so the trader
                    // never has to hunt for a separate "show all" control.
                    onClick={() => onFocus(isFocused ? null : g.key)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onFocus(isFocused ? null : g.key);
                      }
                    }}
                  >
                    <td className="bd-pb-cell bd-pb-cell-l">
                      <div className="bd-pb-name">{g.label}</div>
                    </td>
                    <td className="bd-pb-cell">
                      <span className={sampleClass(s.n)}>n={s.n}</span>
                    </td>
                    <td className="bd-pb-cell">
                      <WinRateCell rate={s.winRate} tone="backtest" />
                    </td>
                    <td
                      className={`bd-pb-cell bd-pb-strong ${
                        s.expectancy != null && s.expectancy < 0 ? 'bd-pb-neg' : 'bd-pb-pos'
                      }`}
                    >
                      {s.expectancy == null ? '—' : signed(s.expectancy, 2)}
                    </td>
                    <td
                      className={`bd-pb-cell bd-pb-strong ${
                        s.net < 0 ? 'bd-pb-neg' : 'bd-pb-pos'
                      }`}
                    >
                      {signed(s.net, 1)}
                    </td>
                    <td className="bd-pb-cell">{num(s.profitFactor, 2)}</td>
                    {/* ⚠ Beta's threshold: only an edge ratio at or above 1.2 is
                        coloured. Below that the winners are not meaningfully
                        bigger than the losers, whatever the win rate says. */}
                    <td
                      className={`bd-pb-cell${
                        s.edgeRatio != null && s.edgeRatio >= 1.2 ? ' bd-pb-pos' : ''
                      }`}
                    >
                      {num(s.edgeRatio, 2)}
                    </td>
                    <td className="bd-pb-cell bd-pb-excursion">
                      {num(s.avgMfe, 1)} / {num(s.avgMae, 1)}
                    </td>
                    <td className="bd-pb-cell">
                      <Spark series={sparkline(g)} />
                    </td>
                  </tr>

                  {l ? (
                    <tr className="bd-pb-liverow">
                      <td className="bd-pb-livetag">↳ Live execution</td>
                      {/* ⚠ Beta prints only "real fills" here. The count is added
                          because a live win rate over three fills is not a rate,
                          and this table's whole job is to stop a number being
                          read as more solid than it is. */}
                      <td className="bd-pb-livemeta">real fills · n={l.n}</td>
                      <td className="bd-pb-cell">
                        <WinRateCell rate={l.winRate} tone="live" />
                      </td>
                      <td className="bd-pb-livegap" colSpan={6}>
                        {/* 🚨 No signed gap — see the header. Beta's own copy is
                            kept verbatim for the un-comparable case. */}
                        {s.winRate == null ? (
                          <span className="bd-pb-muted">
                            no decided backtests to compare against
                          </span>
                        ) : (
                          <span className="bd-pb-muted">
                            backtest win rate on the same pattern: {pct(s.winRate, 0)}
                          </span>
                        )}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
